import { SELECTORS, EVENTS } from '@bs/constants';
import { MessageBus } from '@bs/utils';

/**
 * A class for the Areas of Impact Tabs component
 */
export default class AreasOfImpactTabs extends MessageBus {
  /**
   * Constructor for the AreasOfImpactTabs class.
   * @param {HTMLElement} element - REQUIRED - the component's root element
   */
  constructor(element) {
    super();

    /**
     * Root DOM node passed into the constructor.
     * @type {HTMLElement}
     */
    this.element = element;

    this.init();
  }

  /**
   * Bootstraps the component by chaining the three setup steps.
   * @returns {AreasOfImpactTabs} A reference to the current instance of the class
   * @chainable
   */
  init() {
    this.cacheDomReferences().setupHandlers().enable();

    return this;
  }

  /**
   * Queries and stores all required DOM references scoped to this instance.
   * @returns {AreasOfImpactTabs} A reference to the current instance of the class
   * @chainable
   */
  cacheDomReferences() {
    /** Desktop accordion rows (hidden on mobile via CSS). @type {HTMLElement[]} */
    this.desktopItems = [
      ...this.element.querySelectorAll(`.${SELECTORS.AREAS_OF_IMPACT_ITEM}`)
    ];

    /** Mobile accordion cards (hidden on desktop via CSS). @type {HTMLElement[]} */
    this.mobileCards = [
      ...this.element.querySelectorAll(
        `.${SELECTORS.AREAS_OF_IMPACT_ITEM_CARD}`
      )
    ];

    /** Right-hand visual slide panels (desktop only). @type {HTMLElement[]} */
    this.visualSlides = [
      ...this.element.querySelectorAll(
        `.${SELECTORS.AREAS_OF_IMPACT_VISUAL_SLIDE}`
      )
    ];

    this.breakpoints = {
      desktop: SELECTORS.MAX_DESKTOP
    };

    /**
     * Tracks whether the viewport is below the desktop breakpoint.
     * Mirrors the pattern used in SolutionsModule.
     * @type {MediaQueryList}
     */
    this.mediaQuery = window.matchMedia(this.breakpoints.desktop);

    return this;
  }

  /**
   * Pre-binds event handlers so the same reference can be used for
   * both addEventListener and removeEventListener.
   * @returns {AreasOfImpactTabs} A reference to the current instance of the class
   * @chainable
   */
  setupHandlers() {
    this.handleBreakpointChange = this.onBreakpointChange.bind(this);
    this.handleItemHover = this.onItemHover.bind(this);
    this.handleItemClick = this.onItemClick.bind(this);
    this.handleItemKeydown = this.onItemKeydown.bind(this);
    this.handleCardToggle = this.onCardToggle.bind(this);

    return this;
  }

  /**
   * Attaches all event listeners and runs the initial breakpoint check.
   * @returns {AreasOfImpactTabs} A reference to the current instance of the class
   * @chainable
   */
  enable() {
    this.mediaQuery.addEventListener(
      EVENTS.CHANGE,
      this.handleBreakpointChange
    );

    this.desktopItems.forEach((item) => {
      item.addEventListener(EVENTS.MOUSEENTER, this.handleItemHover);
      item.addEventListener(EVENTS.CLICK, this.handleItemClick);
      item.addEventListener(EVENTS.KEY_DOWN, this.handleItemKeydown);
    });

    this.mobileCards.forEach((card) => {
      card
        .querySelector(`.${SELECTORS.AREAS_OF_IMPACT_CARD_TOGGLE}`)
        ?.addEventListener(EVENTS.CLICK, this.handleCardToggle);
    });

    // Set correct initial state on page load.
    this.onBreakpointChange();

    return this;
  }

  /**
   * Called on viewport resize across the desktop breakpoint.
   * Desktop → ensures the first row is active and collapses any open card.
   * Mobile  → resets all desktop active states.
   * @returns {AreasOfImpactTabs} A reference to the current instance of the class
   */
  onBreakpointChange() {
    if (!this.mediaQuery.matches) {
      const hasActive = this.desktopItems.some((item) =>
        item.classList.contains(SELECTORS.IS_ACTIVE)
      );

      if (!hasActive) {
        this.activateDesktopItem(this.desktopItems.at(0));
      }

      // Collapse any cards that were open on mobile.
      this.collapseAllCards();
    } else {
      // Mobile: strip desktop active states so CSS drives visibility cleanly.
      this.desktopItems.forEach((item) =>
        item.classList.remove(SELECTORS.IS_ACTIVE)
      );
      this.visualSlides.forEach((slide) =>
        slide.classList.remove(SELECTORS.IS_ACTIVE)
      );
    }

    return this;
  }

  /**
   * Activates the hovered desktop row and its matching visual slide.
   * No-ops on mobile/tablet.
   * @param {MouseEvent} event A reference to the click event object
   * @returns {AreasOfImpactTabs} A reference to the current instance of the class
   */
  onItemHover(event) {
    // mediaQuery.matches → below desktop breakpoint → skip.
    if (this.mediaQuery.matches) return this;

    this.activateDesktopItem(event.currentTarget);

    return this;
  }

  /**
   * Navigates to the configured tab link when a desktop row is clicked.
   * Mobile rows are hidden, so mobile card accordion behavior remains unchanged.
   * @param {MouseEvent} event A reference to the click event object
   * @returns {AreasOfImpactTabs} A reference to the current instance of the class
   */
  onItemClick(event) {
    // mediaQuery.matches → below desktop breakpoint → skip.
    if (this.mediaQuery.matches) return this;

    this.goToItemLink(event.currentTarget);

    return this;
  }

  /**
   * Supports keyboard activation for desktop rows that behave as links.
   * @param {KeyboardEvent} event A reference to the keydown event object
   * @returns {AreasOfImpactTabs} A reference to the current instance of the class
   */
  onItemKeydown(event) {
    // mediaQuery.matches → below desktop breakpoint → skip.
    if (this.mediaQuery.matches) return this;

    if (![EVENTS.KEY_ENTER, EVENTS.KEY_SPACE].includes(event.key)) return this;

    event.preventDefault();
    this.goToItemLink(event.currentTarget);

    return this;
  }

  /**
   * Toggles the tapped mobile accordion card.
   * Collapses any other open card before expanding the target.
   * No-ops on desktop.
   * @param {MouseEvent} event A reference to the click event object
   * @returns {AreasOfImpactTabs} A reference to the current instance of the class
   */
  onCardToggle(event) {
    // !mediaQuery.matches → desktop → skip.
    if (!this.mediaQuery.matches) return this;

    const toggle = event.currentTarget;
    const card = toggle.closest(`.${SELECTORS.AREAS_OF_IMPACT_ITEM_CARD}`);
    const isExpanded = card?.classList.contains(SELECTORS.IS_EXPANDED);

    this.collapseAllCards();

    if (!isExpanded) {
      this.expandCard(card);
    }

    return this;
  }

  // ─── Helpers ───────────────────────────────────────────────────────────────

  /**
   * Marks a desktop row as active and shows the corresponding visual slide.
   * Removes the active state from all other rows and slides first.
   * @param {HTMLElement|undefined} item - The row element to activate
   * @returns {AreasOfImpactTabs} A reference to the current instance of the class
   */
  activateDesktopItem(item) {
    if (!item) return this;

    const { area } = item.dataset;

    this.desktopItems.forEach((i) => i.classList.remove(SELECTORS.IS_ACTIVE));
    this.visualSlides.forEach((s) => s.classList.remove(SELECTORS.IS_ACTIVE));

    item.classList.add(SELECTORS.IS_ACTIVE);

    this.element
      .querySelector(
        `.${SELECTORS.AREAS_OF_IMPACT_VISUAL_SLIDE}[data-area="${area}"]`
      )
      ?.classList.add(SELECTORS.IS_ACTIVE);

    return this;
  }

  /**
   * Opens the link attached to a desktop row.
   * @param {HTMLElement} item - The row element to navigate from
   * @returns {AreasOfImpactTabs} A reference to the current instance of the class
   */
  goToItemLink(item) {
    const { tabLink, tabTarget } = item?.dataset || {};

    if (!tabLink) return this;

    if (tabTarget && tabTarget !== '_self') {
      window.open(tabLink, tabTarget);
    } else {
      window.location.href = tabLink;
    }

    return this;
  }

  /**
   * Expands a single mobile accordion card and updates its toggle button state.
   * @param {HTMLElement} card A reference to the current card to expand
   * @returns {AreasOfImpactTabs} A reference to the current instance of the class
   */
  expandCard(card) {
    if (!card) return this;

    const toggle = card.querySelector(
      `.${SELECTORS.AREAS_OF_IMPACT_CARD_TOGGLE}`
    );
    const icon = card.querySelector(`.${SELECTORS.AREAS_OF_IMPACT_CARD_ICON}`);

    card.classList.add(SELECTORS.IS_EXPANDED);
    toggle?.setAttribute(SELECTORS.ARIA_EXPANDED, 'true');

    if (icon) icon.textContent = '−';

    return this;
  }

  /**
   * Collapses all mobile accordion cards and resets their toggle button state.
   * @returns {AreasOfImpactTabs} A reference to the current instance of the class
   */
  collapseAllCards() {
    this.mobileCards.forEach((card) => {
      const toggle = card.querySelector(
        `.${SELECTORS.AREAS_OF_IMPACT_CARD_TOGGLE}`
      );
      const icon = card.querySelector(
        `.${SELECTORS.AREAS_OF_IMPACT_CARD_ICON}`
      );

      card.classList.remove(SELECTORS.IS_EXPANDED);
      toggle?.setAttribute(SELECTORS.ARIA_EXPANDED, 'false');

      if (icon) icon.textContent = '+';
    });

    return this;
  }
}
